import React, {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";

export interface SearchBarHandle {
  clear: () => void;
}

interface SearchBarProps {
  onSearch: (query: string) => void;
  isSearching: boolean;
}

export const SearchBar = forwardRef<SearchBarHandle, SearchBarProps>(
  function SearchBar({ onSearch, isSearching }, ref) {
    const [value, setValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const debouncedSearch = useDebouncedCallback(onSearch, 300);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setValue(newValue);
        debouncedSearch(newValue.trim());
      },
      [debouncedSearch]
    );

    const handleClear = useCallback(() => {
      debouncedSearch.cancel();
      setValue("");
      onSearch("");
      inputRef.current?.focus();
    }, [onSearch, debouncedSearch]);

    useImperativeHandle(ref, () => ({ clear: handleClear }), [handleClear]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Escape" && value) {
          handleClear();
        }
      },
      [value, handleClear]
    );

    return (
      <div className="w-full">
        <div className="flex items-center px-3 py-2 border border-gray-200 rounded-lg bg-white hover:border-gray-300 focus-within:border-gray-400 focus-within:ring-2 focus-within:ring-gray-100 focus-within:shadow-sm transition-all duration-150">
          {isSearching ? (
            <div className="w-4 h-4 mr-2 animate-spin border-2 border-gray-300 border-t-blue-600 rounded-full" />
          ) : (
            <MagnifyingGlass className="w-4 h-4 mr-2 text-gray-400" />
          )}
          <input
            ref={inputRef}
            type="text"
            placeholder="Search files..."
            className="flex-1 outline-none text-sm placeholder:text-gray-400"
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          {value && (
            <button
              onClick={handleClear}
              className="ml-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }
);
